/**
 * @file TextConversation.jsx
 * @description TextConversation component for displaying and managing text conversations.
 * @component TextConversation
 * @exports TextConversation - Main component for displaying and managing text conversations.
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getTextInfo } from "../../../services/agentModule/text/text-conversation";
import { Box, Button, Container, Chip, Alert, Typography } from "@mui/material";

import CampaignIcon from "@mui/icons-material/Campaign";
import CardComponent from "../../../common/components/CardComponent";
import { getUsers } from "../../../services/adminModule/users/userServices";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";
import {
  SIZES,
  VARIANTS,
  BUTTON_COLORS,
  STATUS,
  FONT_WEIGHT,
  DISPLAY,
} from "../../../common/constants/commonConstants";
import EndConversationDailog from "../common/EndConversationDailog.jsx";
import {
  TEXT_ERRORS,
  TEXT_STRING,
  TEXT_SUCCUSS,
} from "../../../common/constants/agentModule/textConstants";
import AssignModal from "../common/AssignModal";
import FilterBar from "../common/FilterBar";
import AgentLayout from "../common/AgentLayout";
import CustomerProfile from "../../callsModule/common/TabProfile";
import CustomerNotes from "../../callsModule/common/TabNotes";
import CustomerHistory from "../../callsModule/common/TabHistory";
import CustomerCallsHistory from "../../callsModule/common/TabCallsHistory";
import CustomerEmails from "../../callsModule/common/TabEmails";
import { EMAIL_STRING } from "../../../common/constants/agentModule/emailContants";
import TabConversation from "../../callsModule/common/TabConversation";
import CustomerChats from "../../callsModule/common/TabChats.jsx";
import CustomerTexts from "../../callsModule/common/TabTexts";
import CustomerDataset from "../../callsModule/common/TabDataset.jsx";
import { useAgentTextActions } from "../agentModel/useAgentTextActions";
import SendText from "../../callsModule/common/SendText.jsx";
import SendEmail from "../../callsModule/common/SendEmail.jsx";
import AddRelatedRecord from "../../callsModule/common/AddRelatedRecord.jsx";

const TextConversation = () => {
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openEndConversationDailog, setOpenEndConversationDailog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState(0); // Default to Active
  const [filterReview, setFilterReview] = useState(0);
  const [filterAssignedTo, setFilterAssignedTo] = useState(-1);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const currentUserEmail = JSON.parse(localStorage.getItem("user")); // Assuming you store user info in localStorage
  const agentemail = currentUserEmail?.emailaddress || ""; // Get email if available

  const {
    handleAssignClick: agentHandleAssignClick,
    handleEndConversationClick: agentHandleEndConversationClick,
  } = useAgentTextActions();

  const tabs = useMemo(
    () => [
      {
        label: "Conversation",
        value: "Conversation",
        component: <TabConversation customerguid={selectedCustomer} />,
      },
      {
        label: "Profile",
        value: "Profile",
        component: (
          <CustomerProfile customerguid={selectedCustomer} agentType="Text" />
        ),
      },
      {
        label: "Dataset",
        value: "Dataset",
        component: (
          <CustomerDataset customerguid={selectedCustomer} />
        ),
      },
      {
        label: "Notes",
        value: "Notes",
        component: <CustomerNotes customerguid={selectedCustomer} />,
      },
      {
        label: "History",
        value: "History",
        component: <CustomerHistory customerguid={selectedCustomer} />,
      },
      { label: "Payments", value: "Payment", component: <div>Payment</div> },
      {
        label: "Calls",
        value: "Calls",
        component: <CustomerCallsHistory customerguid={selectedCustomer} />,
      },
      {
        label: "Texts",
        value: "Text",
        component: <CustomerTexts customerguid={selectedCustomer} />,
      },
      {
        label: "Emails",
        value: "Emails",
        component: <CustomerEmails customerguid={selectedCustomer}/>,
      },
      { label: "Chats", value: "Chat", component: <CustomerChats customerguid={selectedCustomer} /> },
    ],
    [selectedCustomer]
  );

  useEffect(() => {
    const fetchTextConversation = async () => {
      try {
        setIsLoading(true);
        const fetchedEmailRecords = await getTextInfo();
        if (fetchedEmailRecords && fetchedEmailRecords.length > 0) {
          setCustomers(fetchedEmailRecords);
        }
      } catch (error) {
        console.error(TEXT_ERRORS.FETCH_TEXT_INFO_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchUserDetails = async () => {
      try {
        const users = await getUsers();
        if (users) setUsers(users);
      } catch (error) {
        console.error(TEXT_ERRORS.FETCH_USER_INFO_ERROR, error);
      }
    };

    fetchTextConversation();
    fetchUserDetails();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const matchesSearch = searchTerm
        ? customer.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.campaignname.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesFilter =
        filterStatus === 1
          ? customer.convend === 1
          : filterStatus === 0
          ? customer.convend === 0
          : true;

      const matchesReview =
        filterReview === 1
          ? customer.needsreview === 0
          : filterReview === 0
          ? customer.needsreview === 1
          : true;

      const matchesAssignedTo =
        filterAssignedTo === 0
          ? customer.assignedto === agentemail // Me
          : filterAssignedTo === 1
          ? customer.assignedto && customer.assignedto !== agentemail // Others
          : filterAssignedTo === 2
          ? customer.assignedto === null // Unassigned
          : true;
      return (
        matchesSearch && matchesFilter && matchesReview && matchesAssignedTo
      );
    });
  }, [
    customers,
    searchTerm,
    filterStatus,
    filterReview,
    filterAssignedTo,
    agentemail,
  ]);

  useMemo(() => {
    setSelectedCustomer(null);
  }, [searchTerm, filterStatus, filterReview, filterAssignedTo]);

  const handleEndConversationClick = async (customerguid) => {
    setOpenEndConversationDailog(true);
    setSelectedCustomer(customerguid);
  };

  const handleEditClick = (customerguid) => {
    setSelectedCustomer(customerguid);
  };

  const handleAssignClick = async (customerguid) => {
    setOpenAssignModal(true);
    setSelectedCustomer(customerguid);
  };

  const handleConfirmEndConversation = async () => {
    setOpenEndConversationDailog(false);
    setIsLoading(true);
    const result = await agentHandleEndConversationClick(selectedCustomer);
    if (result.success) {
      refreshTextInfo();
      setSelectedCustomer(null);
      setSuccessMessage(result.message);
    } else {
      // Optionally handle error
    }
    setIsLoading(false);
  };

  const handleUpdateAssignee = async (id, newAssignee) => {
    setIsLoading(true);
    const result = await agentHandleAssignClick(id, newAssignee);
    setOpenAssignModal(false);
    if (result.success) {
      refreshTextInfo();
      setSuccessMessage(result.message);
    } else {
      // Optionally handle error
    }
    setIsLoading(false);
  };

  const refreshTextInfo = async () => {
    try {
      setIsLoading(true);
      const fetchedTextRecords = await getTextInfo();
      if (fetchedTextRecords) setCustomers(fetchedTextRecords);
    } catch (error) {
      console.error(TEXT_ERRORS.FETCH_TEXT_INFO_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderTextConversation = useCallback(() => {
    return filteredCustomers.map((customer) => (
      <CardComponent
        sx={{
          mb: 2,
          cursor: "pointer",
          border:
            selectedCustomer === customer.customerguid
              ? "2px solid #1976d2"
              : "1px solid #ccc",
          backgroundColor:
            selectedCustomer === customer.customerguid ? "#e3f2fd" : "#fff",
        }}
        onClick={() => handleEditClick(customer.customerguid)}
        key={customer.customerdataid}
        title={
          <Box sx={{ display: DISPLAY.FLEX, justifyContent: "space-between" }}>
            <Box sx={{ fontSize: "14px" }}>
              {customer.firstname + " " + customer.lastname}
            </Box>
            <Chip
              icon={<CampaignIcon />}
              label={customer.campaignname}
              size={SIZES.SMALL}
              sx={{ mx: 1, fontWeight: FONT_WEIGHT.NORMAL }}
            />
          </Box>
        }
      />
    ));
  }, [filteredCustomers,selectedCustomer]);


   const menuAction = [
    {
      label: TEXT_STRING.ASSIGN,
      onClick: () => handleAssignClick(selectedCustomer)
    },
    {
      label: TEXT_STRING.END_CONVERSATION,
      onClick: () => handleEndConversationClick(selectedCustomer)
    },
    {
      component: (
        <SendText customerguid={selectedCustomer} />
      ),
    },
    {
      component: (
        <SendEmail customerguid={selectedCustomer} />
      ),
    },{ component: (
        <AddRelatedRecord customerguid={selectedCustomer} />
      ),
    }
  ];

  return (
    <Container maxWidth={false} disableGutters>
      {successMessage && (
        <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {isLoading && <ProgressOverlay />}
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterReview={filterReview}
        setFilterReview={setFilterReview}
        filterAssignedTo={filterAssignedTo}
        setFilterAssignedTo={setFilterAssignedTo}
      />

      <AgentLayout
        leftPanel={renderTextConversation()}
        tabConfig={selectedCustomer ? tabs : []}
        // actions={selectedCustomer ? actions : null}
        menuActions={selectedCustomer ? menuAction : null}
        defaultTab="Conversation"
        title={selectedCustomer ? "Customer Information" : ""}
        leftPanelTitle="Inbox Panel"
      />

      {openAssignModal && (
        <AssignModal
          open={openAssignModal}
          customerId={selectedCustomer}
          users={users}
          assignedCust={
            customers.find((c) => c.customerguid === selectedCustomer)
              ?.assignedto
          }
          onAssign={handleUpdateAssignee}
          onClose={() => setOpenAssignModal(false)}
        />
      )}
      {/* Confirmation Dialog */}
      <EndConversationDailog
        open={openEndConversationDailog}
        onClose={() => setOpenEndConversationDailog(false)}
        onConfirm={handleConfirmEndConversation}
      />
    </Container>
  );
};

export default TextConversation;
